import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import moment from 'moment';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  EmailRounded,
  ChatOutlined,
  MoreVert,
  Close,
  Edit,
  Save,
  Event,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Grid,
  IconButton,
  Paper,
  Slide,
  Theme,
  Typography,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  CircularProgress,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChipButtonMenu } from '../drawers/ChipButtonMenu';
import { AvatarsAutocompleteInput, AvatarInputProps } from '../table';
import {
  DateChip,
  NoMaxWidthTooltip,
  TagInputProps,
  TagsAutocompleteInput,
} from '..';
import { useItemColors } from '../../utils/item.utils';
import {
  UseMutation,
  UseLazyQuery,
  UseQueryState,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { IconCircle, IconType } from './IconCircle';
// import { ItemIconCollapse } from './ItemIconCollapse';
import {
  ItemDate,
  ItemDateCollapse,
  getDefaultIconByType,
  getDefaultTooltipByType,
} from './ItemDateCollapse';

export interface ItemProps {
  documentId?: string;
  data?: any;
  title: string;
  msgs?: number;
  cmts?: number;
  numChildren?: number;
  childrenPastDue?: number;
  childrenAtRisk?: number;
  statusCount?: { [key: string]: number };
  due_date?: Date;
  start_date?: Date;
  range?: boolean;
  complete?: boolean;
  onDateChange?: Function;
  onAssignmentChange?: Function;
  onItemClick?: Function;
  onMailClick?: Function;
  onChatClick?: Function;
  menuItems?: { tooltip: string; icon: JSX.Element; onClick: Function }[];
  selected?: boolean;
  highlighted?: boolean;
  showDetails?: boolean;
  icon?: 'task' | 'event' | 'coordination';
  icons?: IconType[];
  dates?: ItemDate[];
  edit?: Function;
  isLoading?: boolean;
  expandParent?: Function;
  disabled?: boolean;
  template?: boolean;
  tags?: TagInputProps;
  assignees?: AvatarInputProps;
  patchMutation?: UseMutation<any>;
  lazyQuery?: UseLazyQuery<any>;
  subscription?: UseQueryState<any>;
  subscriptionParams?: object;
  subscriptionTransform?: (values: any) => any;
  reloadParent?: Function;
  onStatusChange?: Function;
  statuses?: any[];
}

const sidebarwidth = '20px';
const borderwidth = '1px';
const ITEMSTATUS_COMPLETE = 3;
export const Item = forwardRef(
  (
    {
      documentId,
      data,
      title,
      msgs = 0,
      cmts = 0,
      numChildren = 0,
      childrenPastDue = 0,
      childrenAtRisk = 0,
      statusCount,
      due_date,
      start_date,
      complete = false,
      onDateChange = () => {},
      onAssignmentChange = () => {},
      onMailClick = () => {},
      onChatClick = () => {},
      menuItems,
      selected,
      highlighted = false,
      icon = 'task',
      icons,
      dates,
      showDetails = false,
      edit,
      isLoading,
      expandParent,
      disabled = false,
      template = false,
      assignees,
      tags,
      patchMutation,
      lazyQuery,
      subscription,
      subscriptionParams,
      subscriptionTransform = (values: any) => values,
      reloadParent = () => {},
      statuses,
      onStatusChange = () => {},
    }: ItemProps,
    ref
  ) => {
    const [patch, patchResult] = (patchMutation && patchMutation()) || [
      () => {},
      {},
    ];
    const [reload, reloadResult] = (lazyQuery && lazyQuery()) || [() => {}];
    const { data: itemCache } =
      (subscription &&
        subscriptionParams &&
        subscription(subscriptionParams)) ||
      {};

    const triggerReload = async () => {
      if (subscriptionParams) {
        await reload(subscriptionParams);
      }
    };

    const theme = useTheme();
    const downSm = useMediaQuery(theme.breakpoints.down('sm'));
    const upMd = useMediaQuery(theme.breakpoints.up('md'));
    const upLg = useMediaQuery(theme.breakpoints.up('lg'));

    const scrollRef = useRef<HTMLElement>(null);
    const scrollToRef = () => {
      if (scrollRef && scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };
    const iconRef = useRef<HTMLDivElement>(null);
    const dateChipRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      expandParent: () => {
        expandParent && expandParent();
      },
      triggerReload: () => {
        triggerReload();
      },
      scrollRef: scrollRef,
    }));

    useEffect(() => {
      if (selected) {
        scrollToRef();
      }
    }, [selected]);
    const [itemValues, setItemValues] = useState<any>({
      title,
      start_date: dates?.[0]?.start,
      due_date: dates?.[0]?.end,
      append: dates?.[0]?.append,
      dateDisabled: dates?.[0]?.disabled,
      onDateChange: dates?.[0]?.onDateChange,
      complete,
      assignees: assignees?.data,
      status: data?.status_r?.name,
    });

    useEffect(() => {
      const item: any = subscriptionTransform(itemCache);
      if (item?.id) {
        setItemValues((pv: any) => ({
          ...pv,
          title: item?.title,
          start_date: item?.dates?.[0]?.start,
          due_date: item?.dates?.[0]?.end,
          append: item?.dates?.[0]?.append,
          dateDisabled: item?.dates?.[0]?.disabled,
          onDateChange: item?.dates?.[0]?.onDateChange,
          complete: item?.status_r?.id
            ? item?.status_r?.id === ITEMSTATUS_COMPLETE
            : pv?.complete,
          status: item?.status_r?.name || pv?.status,
        }));
      }
    }, [itemCache]);

    useEffect(() => {
      const item: any = subscriptionTransform(patchResult?.data);
      if (item) {
        setItemValues((pv: any) => ({
          ...pv,
          title: item?.title || pv.title,
          start_date: item?.dates?.[0]?.start,
          due_date: item?.dates?.[0]?.end,
          append: item?.dates?.[0]?.append,
          dateDisabled: item?.dates?.[0]?.disabled,
          onDateChange: item?.dates?.[0]?.onDateChange,
          complete: item?.status_r?.id
            ? item?.status_r?.id === ITEMSTATUS_COMPLETE
            : pv?.complete,
          status: item?.status_r?.name || pv?.status,
        }));
      }
    }, [patchResult]);

    useEffect(() => {
      setItemValues((pv: any) => ({
        ...pv,
        complete,
        status: data?.status_r?.name || pv?.status?.label,
        // assignees: assignees?.data,
      }));
    }, [complete, data?.masked]);
    const [menu, setMenu] = useState<boolean>(false);
    const containerRef = useRef(null);

    const {
      item: {
        border: borderColor,
        background: backgroundColor,
        font: fontColor,
      },
    } = useItemColors({
      date: itemValues?.due_date || itemValues?.start_date,
      complete: itemValues?.complete,
    });

    const [hover, setHover] = useState<boolean>(false);
    const [iconHover, setIconHover] = useState<boolean>(false);
    const [prevValue, setPrevValue] = useState<string>(title);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [iconAnchorEl, setIconAnchorEl] =
      useState<HTMLDivElement | null>(null);
    const [dateExpand, setDateExpand] = useState<boolean>(false);

    return (
      <Box
        sx={{
          width: '100%',
          mr: { xs: 0, sm: 1 },
          zIndex: selected === false ? -1 : undefined,
        }}
      >
        <Paper
          id={documentId}
          ref={ref as any}
          elevation={highlighted ? 10 : 1}
          onMouseEnter={() => setHover(true && !disabled)}
          onMouseLeave={() => setHover(false)}
          sx={{
            // mr: { xs: 0, sm: 1 },
            borderLeft: `${sidebarwidth} solid ${borderColor}`,
            borderTop: `${borderwidth} solid ${borderColor}`,
            borderBottom: `${borderwidth} solid ${borderColor}`,
            borderRight: `${borderwidth} solid ${borderColor}`,
            backgroundColor: backgroundColor,
            // width: '100%',
            zIndex: selected ? (theme: Theme) => theme.zIndex.drawer - 10 : 0,
            boxShadow: selected
              ? '0 0 0 99999px rgba(0, 0, 0, .7)'
              : highlighted
              ? '0px 6px 6px -3px rgb(118, 176, 65,0.8), 0px 10px 14px 1px rgb(118, 176, 65, 0.5), 0px 4px 18px 3px rgb(118, 176, 65, 0.5)'
              : '',
          }}
        >
          <span ref={scrollRef}></span>

          <Grid container alignContent={'center'} spacing={0}>
            <Grid
              item
              container
              spacing={0}
              alignItems={'center'}
              sx={upMd ? { width: 'fit-content' } : { flex: 1 }}
              order={1}
            >
              <Grid item>
                <IconButton
                  size={downSm ? 'medium' : 'large'}
                  edge="end"
                  disabled={disabled}
                  color="primary"
                  onClick={async event => {
                    event.stopPropagation();
                    event.preventDefault();
                    onStatusChange(
                      itemValues?.complete
                        ? { id: 2, name: 'In Progress' }
                        : { id: 3, name: 'Complete' },
                      () =>
                        setItemValues((pv: any) => ({
                          ...pv,
                          complete: !pv.complete,
                          status: pv.complete ? 'In Progress' : 'Complete',
                        }))
                    );
                  }}
                >
                  {itemValues?.complete ? (
                    <CheckBox fontSize="small" />
                  ) : (
                    <CheckBoxOutlineBlank fontSize="small" />
                  )}
                </IconButton>
              </Grid>

              {icons && (
                <Grid item ref={iconRef}>
                  <IconButton
                    size={downSm ? 'medium' : 'large'}
                    edge="end"
                    color="primary"
                    disableRipple={true}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      setIconAnchorEl(iconRef.current);
                    }}
                  >
                    <IconCircle icons={icons} />
                  </IconButton>
                </Grid>
              )}
            </Grid>

            {!disabled ? (
              <Grid
                item
                container
                spacing={0}
                sx={{ width: 'fit-content', alignItems: 'center' }}
                order={upLg ? 4 : upMd ? 3 : 2}
              >
                {menuItems && menuItems.length > 0 && menu ? (
                  <Grid item>
                    <Slide direction="left" in={menu}>
                      <Grid container spacing={0}>
                        {menuItems?.map((menuItem, index) => (
                          <Grid item key={index}>
                            <Tooltip title={menuItem.tooltip}>
                              <IconButton
                                size={downSm ? 'medium' : 'large'}
                                edge="end"
                                color="primary"
                                onClick={async event => {
                                  event.stopPropagation();
                                  event.preventDefault();
                                  await menuItem.onClick();
                                  setMenu(false);
                                }}
                              >
                                {menuItem.icon}
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        ))}

                        <Grid item>
                          <Tooltip title={'Close'} key={'close'}>
                            <IconButton
                              size={downSm ? 'medium' : 'large'}
                              aria-label="item messages"
                              edge="end"
                              color="primary"
                              onClick={event => {
                                event.stopPropagation();
                                event.preventDefault();
                                setMenu(false);
                              }}
                              sx={{ mr: 0.5 }}
                            >
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Slide>
                  </Grid>
                ) : (
                  <Grid item>
                    <IconButton
                      size={downSm ? 'medium' : 'large'}
                      aria-label="item messages"
                      edge="end"
                      color="primary"
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        setMenu(true && !disabled);
                      }}
                      sx={{ mr: 0.5 }}
                    >
                      {patchResult.isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <MoreVert />
                      )}
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Grid
                item
                container
                spacing={0}
                sx={{ width: 'fit-content', alignItems: 'center' }}
                order={upLg ? 4 : upMd ? 3 : 2}
              >
                &nbsp;{' '}
              </Grid>
            )}

            <Grid
              item
              {...(!upMd ? { xs: 12 } : {})}
              container
              spacing={0}
              sx={{
                alignItems: 'center',
                ...(upMd ? { flex: 1 } : { ml: 0, pb: 1, pr: 1 }),
              }}
              order={upMd ? 2 : 3}
            >
              {isEditing ? (
                <Grid
                  item
                  xs={12}
                  container
                  spacing={0}
                  {...(downSm ? {} : { sx: { flexWrap: 'nowrap' } })}
                >
                  <Grid
                    item
                    {...(downSm
                      ? { xs: 12 }
                      : { sx: { flex: 1, maxWidth: upMd ? 240 : 'unset' } })}
                  >
                    <StyledTextField
                      fullWidth
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                      }}
                      InputProps={{
                        sx: {
                          fontSize: '1rem',
                          fontWeight: 500,
                        },
                      }}
                      value={itemValues.title}
                      onChange={event =>
                        setItemValues((pv: any) => ({
                          ...pv,
                          title: event.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid
                    item
                    {...(downSm ? { xs: 12 } : { width: 'fit-content' })}
                    container
                    spacing={0}
                  >
                    <Grid item>
                      <Tooltip title={'Save'}>
                        <IconButton
                          size="small"
                          sx={{ zIndex: 1 }}
                          onClick={event => {
                            event.stopPropagation();
                            event.preventDefault();
                            edit && edit({ title: itemValues.title });
                            reloadParent();
                            setIsEditing(false);
                          }}
                        >
                          <Save fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>

                    <Grid item>
                      <Tooltip title={'Cancel'}>
                        <IconButton
                          size="small"
                          sx={{ zIndex: 1 }}
                          onClick={event => {
                            event.stopPropagation();
                            event.preventDefault();
                            setItemValues((pv: any) => ({
                              ...pv,
                              title: prevValue,
                            }));
                            setIsEditing(false);
                            setHover(false);
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid
                  item
                  container
                  spacing={0}
                  alignContent={'center'}
                  alignItems={'center'}
                  sx={{ flexWrap: 'nowrap' }}
                >
                  <Grid item>
                    <Typography
                      variant="body1"
                      color="primary"
                      sx={{
                        boxSizing: 'border-box',
                        minHeight: 33,
                        pt: 0.6,
                        ml: 0.8,
                      }}
                    >
                      {itemValues.title}&nbsp;
                    </Typography>
                  </Grid>
                  {data?.task?.details && data?.task?.details != '' && (
                    <Grid item sx={{ flex: 0 }} alignContent={'center'}>
                      <NoMaxWidthTooltip title={data?.task?.details}>
                        <IconButton
                          size="small"
                          sx={{
                            zIndex: 1,
                            p: 0,
                            cursor: 'default',
                          }}
                          onClick={event => {
                            event.stopPropagation();
                            event.preventDefault();
                          }}
                        >
                          <InfoOutlined
                            fontSize="small"
                            sx={{
                              color: theme => theme.palette.text.primary,
                            }}
                          />
                        </IconButton>
                      </NoMaxWidthTooltip>
                    </Grid>
                  )}
                  {hover && (
                    <Grid item sx={{ flex: 1 }}>
                      <Tooltip title={'Edit title'}>
                        <IconButton
                          size="small"
                          sx={{ zIndex: 1 }}
                          onClick={event => {
                            event.stopPropagation();
                            event.preventDefault();
                            setPrevValue(itemValues.title);
                            setIsEditing(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>

            <Grid
              item
              {...(!upLg ? { xs: 12 } : {})}
              container
              spacing={0}
              order={upLg ? 3 : 4}
              sx={{
                alignItems: 'center',
                ...(upLg ? { width: 'fit-content' } : { ml: 0, pb: 1, pr: 1 }),
              }}
            >
              {showDetails &&
                itemValues?.status !== 'Complete' &&
                statusCount &&
                Object.keys(statusCount).length > 0 && (
                  <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ pl: 1 }}>
                    <Tooltip
                      title={
                        <span>
                          {`${childrenPastDue} Deadline${
                            childrenPastDue > 1 ? 's' : ''
                          } Missed`}
                          <br />
                          {`${childrenAtRisk} Deadline${
                            childrenAtRisk > 1 ? 's' : ''
                          } Approaching`}
                          <br />
                          {`${statusCount?.['Complete'] || 0} Completed`}
                        </span>
                      }
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight:
                            childrenPastDue > 0 || childrenAtRisk > 0
                              ? 700
                              : undefined,
                          color: theme =>
                            childrenPastDue > 0
                              ? theme.palette.error.main
                              : childrenAtRisk > 0
                              ? theme.palette.warning.main
                              : 'inherit',
                        }}
                      >
                        <span style={{ color: 'black', fontWeight: 400 }}>{`${
                          statusCount?.['Complete'] || 0
                        }/`}</span>
                        {`${Object.values(statusCount)?.reduce(
                          (acc, b) => acc + b,
                          0
                        )}`}
                      </Typography>
                    </Tooltip>
                  </Grid>
                )}
              {cmts > 0 && (
                <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ pl: 1 }}>
                  <IconButton
                    size="large"
                    aria-label="item messages"
                    edge="end"
                    color="primary"
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      onChatClick();
                    }}
                  >
                    <Badge badgeContent={cmts} color="secondary">
                      <ChatOutlined />
                    </Badge>
                  </IconButton>
                </Grid>
              )}
              {msgs > 0 && (
                <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ pl: 1 }}>
                  <IconButton
                    size="large"
                    aria-label="item messages"
                    edge="end"
                    color="primary"
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      onMailClick();
                    }}
                  >
                    <Badge badgeContent={msgs} color="secondary">
                      <EmailRounded />
                    </Badge>
                  </IconButton>
                </Grid>
              )}
              <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ ml: 1 }}>
                <TagsAutocompleteInput
                  {...{
                    ...tags,
                    disabled: disabled,
                    hover: hover,
                  }}
                />
              </Grid>
              <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ ml: 1 }}>
                <AvatarsAutocompleteInput
                  {...{
                    ...assignees,
                    disabled: disabled,
                    hover: hover,
                  }}
                />
              </Grid>

              {hover &&
                !template &&
                !itemValues?.complete &&
                !itemValues?.start_date &&
                !itemValues?.due_date && (
                  <Grid
                    item
                    {...(downSm ? { xs: 12 } : {})}
                    sx={{ pl: 1 }}
                    onClick={event => event.stopPropagation()}
                  >
                    <DateChip
                      ref={dateChipRef}
                      value={{
                        start: itemValues?.start_date
                          ? new Date(itemValues?.start_date)
                          : undefined,
                        end: itemValues?.due_date
                          ? new Date(itemValues?.due_date)
                          : undefined,
                      }}
                      onDateChange={async ({
                        start,
                        end,
                      }: {
                        start?: Date;
                        end?: Date;
                      }) => {
                        const updateDates = [
                          { ...(dates?.[0] || {}), start, end },
                          ...(dates?.slice(1, dates?.length) || []),
                        ].sort((a, b) => moment(a.end).diff(b.end));
                        if (itemValues?.onDateChange) {
                          await patch(itemValues.onDateChange({ start, end }));
                        } else {
                          await patch(onDateChange({ start, end }));
                        }
                        await setItemValues((pv: any) => ({
                          ...pv,
                          start_date: updateDates?.[0]?.start,
                          due_date: updateDates?.[0]?.end,
                          append: updateDates?.[0]?.append,
                          dateDisabled: updateDates?.[0]?.disabled,
                          onDateChange: updateDates?.[0]?.onDateChange,
                        }));
                        triggerReload();
                        reloadParent();
                      }}
                      tooltip={'Add Item Start/Due Date(s)'}
                      disabled={disabled}
                    />
                  </Grid>
                )}
              {(itemValues?.start_date || itemValues?.due_date) && !template && (
                <Grid
                  item
                  {...(downSm ? { xs: 12 } : {})}
                  sx={{
                    pl: 1,
                  }}
                >
                  <DateChip
                    ref={dateChipRef}
                    badge={
                      !dateExpand && dates && dates.length > 0
                        ? dates.length - 1
                        : 0
                    }
                    onBadgeClick={() => setDateExpand(true)}
                    value={{
                      start: itemValues?.start_date
                        ? new Date(itemValues?.start_date)
                        : undefined,
                      end: itemValues?.due_date
                        ? new Date(itemValues?.due_date)
                        : undefined,
                    }}
                    onDateChange={async ({
                      start,
                      end,
                    }: {
                      start?: Date;
                      end?: Date;
                    }) => {
                      const updateDates = [
                        { ...(dates?.[0] || {}), start, end },
                        ...(dates?.slice(1, dates?.length) || []),
                      ].sort((a, b) => moment(a.end).diff(b.end));
                      if (itemValues?.onDateChange) {
                        await patch(itemValues.onDateChange({ start, end }));
                        await setItemValues((pv: any) => ({
                          ...pv,
                          start_date: updateDates?.[0]?.start,
                          due_date: updateDates?.[0]?.end,
                          append: updateDates?.[0]?.append,
                          dateDisabled: updateDates?.[0]?.disabled,
                          onDateChange: updateDates?.[0]?.onDateChange,
                        }));
                        triggerReload();
                        reloadParent();
                      }
                    }}
                    complete={itemValues?.complete}
                    // label={`${numDays}d Past Due`}
                    tooltip={getDefaultTooltipByType(
                      dates?.[0]?.type,
                      !!dates?.[0]?.start,
                      !!dates?.[0]?.end
                    )}
                    icon={getDefaultIconByType(dates?.[0]?.type)}
                    disabled={
                      disabled ||
                      itemValues?.dateDisabled ||
                      itemValues?.complete
                    }
                  />
                </Grid>
              )}

              {statuses && statuses.length > 0 && itemValues?.status && (
                <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ pl: 1 }}>
                  <ChipButtonMenu
                    disabled={disabled}
                    label={
                      itemValues?.complete ? 'Complete' : itemValues?.status
                    }
                    menuItems={statuses?.map((mi: any) => ({
                      label: mi.name,
                      onClick: async () => {
                        await onStatusChange(mi, () =>
                          setItemValues((pv: any) => ({
                            ...pv,
                            complete:
                              mi.name.toLocaleLowerCase() === 'complete',
                            status: mi.name,
                          }))
                        );
                      },
                    }))}
                    color={borderColor}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
        {dates && dates.length > 1 && dateChipRef.current && (
          <ItemDateCollapse
            open={dateExpand}
            close={() => setDateExpand(false)}
            dates={dates}
            anchorEl={dateChipRef.current}
            complete={itemValues?.complete}
            updateDates={async ({
              start,
              end,
              index,
            }: {
              start?: Date;
              end?: Date;
              index: number;
            }) => {
              let updateDates = [...dates];
              updateDates[index] = {
                ...updateDates[index],
                start: start || null,
                end: end || null,
              };
              const onDateChange = updateDates?.[index]?.onDateChange;
              updateDates.sort((a, b) => moment(a.end).diff(b.end));
              if (onDateChange) {
                await patch(onDateChange({ start, end }));
                await setItemValues((pv: any) => ({
                  ...pv,
                  start_date: updateDates?.[0]?.start,
                  due_date: updateDates?.[0]?.end,
                  append: updateDates?.[0]?.append,
                  dateDisabled: updateDates?.[0]?.disabled,
                  onDateChange: updateDates?.[0]?.onDateChange,
                }));

                triggerReload();
                reloadParent();
              }
            }}
          />
        )}
      </Box>
    );
  }
);

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    padding: '5px 5px',
  },
}));
